import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Form, FormGroup, Label, Input, Button, Table
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

function groupByMonth(rows = []) {
  const groups = {};
  for (const r of rows) {
    const d = r.draw_created_at || r.participation_date ? new Date(r.draw_created_at || r.participation_date) : null;
    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "Unknown";
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }
  return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

function groupByDay(rows = []) {
  const groups = {};
  for (const r of rows) {
    const d = r.draw_created_at || r.participation_date ? new Date(r.draw_created_at || r.participation_date) : null;
    const key = d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      : "Unknown";
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }
  return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

function makeUserKey(r) {
  return r.user_id || r.player_id || r.email || r.PhoneNumber || r.fullName || r.id || "unknown";
}

function aggregateByUser(rows = []) {
  const map = new Map();
  rows.forEach((r) => {
    const key = makeUserKey(r);
    if (!map.has(key)) {
      map.set(key, { row: r, count: 1 });
    } else {
      map.get(key).count += 1;
    }
  });
  return Array.from(map.values()).map((v) => ({ ...v.row, _count: v.count }));
}

function GameDrawsWinners() {
  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-US", {
        timeZone: "Asia/Amman",
        year: "numeric",
        month: "numeric",
        day: "numeric"
      });
    } catch {
      return String(value);
    }
  };
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [winnersCount, setWinnersCount] = useState(1);
  const [status, setStatus] = useState("");
  const [winners, setWinners] = useState([]);
  const [allWinners, setAllWinners] = useState([]);
  const [error, setError] = useState("");

  const notify = (msg, t = 3500) => {
    setStatus(msg);
    setTimeout(() => setStatus(""), t);
  };

  const fetchWinners = async () => {
    try {
      const params = fromDate && toDate ? `?from=${fromDate}&to=${toDate}` : "";
      const res = await axios.get(`${BASE_URL}/game/winners${params}`);
      setAllWinners(res.data || []);
    } catch {
      setError("Error fetching winners.");
    }
  };

  useEffect(() => { fetchWinners(); }, []);

  const monthlyGroups = useMemo(() => groupByMonth(allWinners), [allWinners]);
  const winnersSummary = useMemo(() => aggregateByUser(winners), [winners]);

  const createAndRunDraw = async (e) => {
    e.preventDefault();
    if (!fromDate || !toDate || !winnersCount) {
      notify("Please fill all fields.");
      return;
    }
    try {
      const created = await axios.post(`${BASE_URL}/game/draws`, {
        fromDate,
        toDate,
        winnersCount: Number(winnersCount)
      });
      const drawId = created.data.id;
      const run = await axios.post(`${BASE_URL}/game/draws/${drawId}/run`);
      setWinners(run.data.winners || []);
      notify(`Draw completed. Winners selected: ${run.data.winnersCount}`);
      fetchWinners();
    } catch {
      notify("Draw failed.");
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Draw System (Lottery)</CardTitle>
            </CardHeader>
            <CardBody>
              {status && <div className="alert alert-info">{status}</div>}
              <Form onSubmit={createAndRunDraw}>
                <Row form>
                  <Col md="4">
                    <FormGroup>
                      <Label>From Date</Label>
                      <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>To Date</Label>
                      <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Number of Winners</Label>
                      <Input type="number" min="1" value={winnersCount} onChange={(e) => setWinnersCount(e.target.value)} />
                    </FormGroup>
                  </Col>
                </Row>
                <Button color="primary" type="submit">Draw Winners</Button>
              </Form>

              {winners.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Draw Results</h5>
                    <Button color="secondary" size="sm" onClick={() => setWinners([])}>Close</Button>
                  </div>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th>Times</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Participation Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winnersSummary.map((w) => (
                        <tr key={`${w.user_id || w.id || w.email || w.PhoneNumber}-${w._count}`}>
                          <td>{w.user_id || "-"}</td>
                          <td>{w._count || 1}</td>
                          <td>{w.fullName || "-"}</td>
                          <td>{w.email || "-"}</td>
                          <td>{w.PhoneNumber || "-"}</td>
                          <td>{formatDate(w.participation_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Winners Records (Monthly)</CardTitle>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form inline className="mb-3" onSubmit={(e) => { e.preventDefault(); fetchWinners(); }}>
                <FormGroup className="mr-2">
                  <Label className="mr-2">From</Label>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </FormGroup>
                <FormGroup className="mr-2">
                  <Label className="mr-2">To</Label>
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </FormGroup>
                <Button color="primary">Filter</Button>
              </Form>

              {monthlyGroups.map(([month, rows]) => (
                <div key={month} className="mb-4">
                  <h6>{month}</h6>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Wins</th>
                        <th>Times</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Participation Date</th>
                        <th>Draw Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupByDay(rows).map(([day, dayRows]) => (
                        <React.Fragment key={day}>
                          <tr>
                            <td colSpan="8" style={{ fontWeight: "bold", background: "#f7f7f7" }}>
                              {day}
                            </td>
                          </tr>
                          {aggregateByUser(dayRows).map((r) => (
                            <tr key={`${day}-${r.user_id || r.id || r.email || r.PhoneNumber}-${r._count}`}>
                              <td>{r.wins_count || 0}</td>
                              <td>{r._count || 1}</td>
                              <td>{r.fullName || "-"}</td>
                              <td>{r.email || "-"}</td>
                              <td>{r.PhoneNumber || "-"}</td>
                              <td>{formatDate(r.participation_date)}</td>
                              <td>{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                      {rows.length === 0 && (
                        <tr><td colSpan="8">No winners.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              ))}
              {monthlyGroups.length === 0 && <div>No winners.</div>}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameDrawsWinners;
